import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AuthCallback from './pages/auth/AuthCallback';
import SSOAccept from './pages/auth/SSOAccept';

// Public Pages
import PublicTracking from './pages/public/PublicTracking';

// Customer Pages
import CustomerDashboard from './pages/customer/CustomerDashboard';
import CreateWaybill from './pages/customer/CreateWaybill';
import WaybillDetail from './pages/customer/WaybillDetail';
import Tracking from './pages/customer/Tracking';
import History from './pages/customer/History';
import Notifications from './pages/customer/Notifications';
import Settings from './pages/customer/Settings';
import Billing from './pages/customer/Billing';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import OperationsHub from './pages/admin/OperationsHub';
import QuoteQueue from './pages/admin/QuoteQueue';
import AcceptedShipments from './pages/admin/AcceptedShipments';
import ShipmentDetail from './pages/admin/ShipmentDetail';
import Reports from './pages/admin/Reports';
import CustomerManagement from './pages/admin/CustomerManagement';
import CustomerBilling from './pages/admin/CustomerBilling';
import StaffManagement from './pages/admin/StaffManagement';
import AdminSettings from './pages/admin/AdminSettings';
import AdminNotifications from './pages/admin/AdminNotifications';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard
    if (user?.role === 'customer') {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

// Public Route (redirects authenticated users)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      return <Navigate to="/admin/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// SSO-aware root redirect: if ?sso_token= is present, route to SSO accept
const SSOAwareRedirect = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get('sso_token')) {
    return <Navigate to={`/sso/accept?sso_token=${params.get('sso_token')}`} replace />;
  }
  return <Navigate to="/login" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/sso/accept" element={<SSOAccept />} />
      <Route path="/track" element={<PublicTracking />} />

      {/* Customer Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/create-waybill" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CreateWaybill />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/waybill/:waybillId" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <WaybillDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tracking" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <Tracking />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <History />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/notifications" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <Notifications />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <Settings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/billing" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <Billing />
          </ProtectedRoute>
        } 
      />

      {/* Admin Routes */}
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/operations" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <OperationsHub />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/quote-queue" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <QuoteQueue />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/accepted-shipments" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <AcceptedShipments />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/shipment/:waybillId" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <ShipmentDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/reports" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/customers" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <CustomerManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/customer/:customerId/billing" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <CustomerBilling />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/staff" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <StaffManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/settings" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <AdminSettings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/notifications" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <AdminNotifications />
          </ProtectedRoute>
        } 
      />

      {/* Default Redirect — SSO-aware */}
      <Route path="/" element={<SSOAwareRedirect />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="bottom-right" 
          toastOptions={{
            style: {
              background: '#0f766e',
              color: '#fff',
              borderRadius: '12px',
            },
          }}
        />
      </AuthProvider>
    </Router>
  );
}

export default App;
